// frontend/src/telas/pdf/PdfSerieHistoria.js

import { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getDadosImovel,
  getDadosSerieHistoria,
  getDadosTabelaHistoricoUsoSolo,
} from "../../services/api/fusio";
import { BASE_URL_GEO } from "../../services/common";
import ImgLogoEmbrapa from "../../assets/logo-embrapa.png";
import ReactToPrint from "react-to-print";
import {
  CelulaTabela,
  ContainerAnoImg,
  ContainerTextoRodape,
  ContainerTextoRodapeH,
  ContainerTitulo,
  CorpoPdf,
  FolhaA4,
  Header,
  ImgsContainer,
  LegendaEstadoContainer,
  LinhaTabela,
  LogoEmbrapa,
  MapsContainer,
  PDFContainer,
  RodapePdf,
  SpanNomeMunicipioFazenda,
  SpanNumeroCar,
  SpanTitulo,
  Tabela,
  TabelaContainer,
  TextoAno,
  TextoAnoTabela,
  TextoCidadeEstadoContainer,
  TextoHeaderTabela,
  TextoInfoTabela,
  TextoLegenda,
  TextoLegendaNota,
  TextoNomeCidade,
  TextoRodape,
  TextoRodapePdf,
} from "./pdf-serie-historia-estilo";
import { Button } from "@mui/material";
import { decrementLoad, incrementLoad } from "../../redux/reducer";
import { EscalaGrafica } from "./EscalaGrafica";

// ============================ CONFIG GEOSERVER (NOVO) ============================
const GEOSERVER_DEFAULT_URL = "https://localhost/geoserver";
const GEOSERVER_BASE = BASE_URL_GEO ?? GEOSERVER_DEFAULT_URL;

const LAYER_USO_1985 = "lair:1985";
const LAYER_USO_2008 = "lair:2008";
const LAYER_USO_2011 = "lair:2011";
const LAYER_USO_2016 = "lair:2016";
const LAYER_USO_2021 = "lair:2021";
const LAYER_MUNICIPIO = "lair:municipio";
const LAYER_IMOVEL = "lair:imovel_area_imovel";


// estilos (se tiver estilos nomeados, ajuste aqui; vazio usa o default)
const STYLE_USO_1985 = "";
const STYLE_USO_2008 = "";
const STYLE_USO_2011 = "";
const STYLE_USO_2016 = "";
const STYLE_USO_2021 = "uso_abc";
const STYLE_MUNICIPIO = "municipio";
const STYLE_IMOVEL = "imovel_destaque";

const USO_LEGENDA_ITEMS = [
  { key: "Pastagem", label: "Pastagem", color: "#BDB70F" },
  { key: "Agricultura", label: "Agricultura", color: "#FFE500" },
  { key: "Agropecuaria", label: "Agropecu\u00e1ria", color: "#CFA29C" },
  { key: "Silvicultura", label: "Silvicultura", color: "#6AA84F" },
  { key: "NaturalNaoVegetada", label: "Natural n\u00e3o vegetada", color: "#9E9E9E" },
  { key: "VegetacaoFlorestal", label: "Vegeta\u00e7\u00e3o florestal", color: "#7F9E23" },
  { key: "VegetacaoNaoFlorestal", label: "Vegeta\u00e7\u00e3o n\u00e3o florestal", color: "#B7E28B" },
  { key: "InfluenciaUrbana", label: "Influ\u00eancia urbana", color: "#FFB3C6" },
  { key: "Agua", label: "\u00c1gua", color: "#9CCAD3" },
];

// Tamanho default das miniaturas
const IMG_W = 160;
const IMG_H = 160;
const IMG_MAPA_CIDADE_W = 140;
const IMG_MAPA_CIDADE_H = 140;
// ===============================================================================

// ------------------------ helpers ------------------------

function agruparEmArrays(array, tam) {
  const arrayResult = [];
  const tamanho = array.length;
  let dif = 0;
  array.flatMap((item, i) => {
    if (i > 0 && i % tam === 0) {
      arrayResult.push(array.slice(i - tam, i));
      dif = 0;
    }
    if (i === tamanho - 1) {
      arrayResult.push(array.slice(i - dif, i + 1));
    }
    dif++;
  });
  return arrayResult;
}

// calcula bbox [ [latMin,lngMin], [latMax,lngMax] ] a partir do GeoJSON
function getBoundsFromGeoJSON(geom) {
  if (!geom || !geom.type || !geom.coordinates) return null;

  let coords = [];
  if (geom.type === "Polygon") {
    coords = geom.coordinates.flat(); // [[lon,lat], ...]
  } else if (geom.type === "MultiPolygon") {
    coords = geom.coordinates.flat(2); // [[lon,lat], ...]
  } else {
    return null;
  }

  const lats = coords.map((c) => c[1]);
  const lngs = coords.map((c) => c[0]);

  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lngMin = Math.min(...lngs);
  const lngMax = Math.max(...lngs);

  return [
    [latMin, lngMin],
    [latMax, lngMax],
  ];
}

const headersFixos = [
  "Pastagem",
  "Agricultura",
  "Agropecuária",
  "Silvicultura",
  "Natural não vegetada",
  "Vegetação florestal",
  "Vegetação não florestal",
  "Água",
  "Infraestrutura",
];

function dePara(de, para) {
  if (de === "Pastagem" && para === "Pastagem natural ou plantada") return true;
  if (de === "Agricultura" && para === "Agricultura") return true;
  if (de === "Agropecuária" && para === "Mosaico de agricultura e pastagem") return true;
  if (de === "Silvicultura" && para === "Silvicultura") return true;
  if (de === "Natural não vegetada" && para === "Áreas não vegetadas") return true;
  if (de === "Vegetação florestal" && para === "Vegetação natural florestal") return true;
  if (de === "Vegetação não florestal" && para === "Vegetação natural não florestal") return true;
  if (de === "Infraestrutura" && para === "Área de influência urbana") return true;
  if (de === "Água" && para === "Água e aquicultura") return true;
  return false;
}

// --------- helpers adicionais p/ WMS (NOVO) ----------
function padBbox(bbox, factor = 0.07) {
  if (!bbox) return null;
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  const dx = (xmax - xmin) * factor;
  const dy = (ymax - ymin) * factor;
  return [
    [ymin - dy, xmin - dx],
    [ymax + dy, xmax + dx],
  ];
}

function bboxToParams(bbox) {
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  return `${xmin},${ymin},${xmax},${ymax}`; // minx,miny,maxx,maxy
}

function lonLatToWebMercator(lon, lat) {
  const originShift = 20037508.34;
  const x = (lon * originShift) / 180.0;
  const y =
    (Math.log(Math.tan(((90 + lat) * Math.PI) / 360.0)) / (Math.PI / 180.0)) *
    (originShift / 180.0);
  return [x, y];
}

function bboxToWebMercator(bbox) {
  const [[ymin, xmin], [ymax, xmax]] = bbox;
  const [minx, miny] = lonLatToWebMercator(xmin, ymin);
  const [maxx, maxy] = lonLatToWebMercator(xmax, ymax);
  return `${minx},${miny},${maxx},${maxy}`;
}

function wmsPngUrl({
  layer,
  style = "",
  bbox,
  width = IMG_W,
  height = IMG_H,
  cql = "",
  srs = "EPSG:4674",
}) {
  const params = new URLSearchParams({
    service: "WMS",
    request: "GetMap",
    version: "1.1.0",
    layers: layer,
    styles: style,
    format: "image/png",
    transparent: "true",
    srs,
    width: String(width),
    height: String(height),
    bbox: bboxToParams(bbox),
  });
  if (cql) params.set("cql_filter", cql);
  return `${GEOSERVER_BASE}/wms?${params.toString()}`;
}
// ------------------------------------------------------

function pickNonEmpty(...valores) {
  for (const valor of valores) {
    const texto = (valor || "").trim();
    if (texto && texto !== "-" && texto !== "--") return texto;
  }
  return "";
}

function formatCidadeEstado(cidade, estado) {
  const city = pickNonEmpty(cidade);
  const uf = pickNonEmpty(estado);
  if (city && uf) return `${city} - ${uf}`;
  return city || uf || "";
}

// Parse robusto para série histórica (continua, caso você ainda use dados do endpoint)
function parseSerieHistoria(serieResp) {
  const obj = Array.isArray(serieResp) ? (serieResp[0] || {}) : (serieResp || {});
  const imagens = [];
  Object.keys(obj).forEach((k) => {
    if (k.startsWith("imagemusocoberturasolo")) {
      const ano = k.replace("imagemusocoberturasolo", "");
      if (/^\d{4}$/.test(ano)) imagens.push({ ano, img: obj[k] });
    } else if (k.startsWith("imagem_uso_terra_")) {
      const m = k.match(/imagem_uso_terra_(\d{4})$/);
      if (m) imagens.push({ ano: m[1], img: obj[k] });
    }
  });
  imagens.sort((a, b) => Number(a.ano) - Number(b.ano));
  return { meta: obj, imagens };
}

// Parse robusto da tabela “flat”
function parseTabelaHistorico(resp) {
  let tabelaEntry;
  if (Array.isArray(resp)) {
    tabelaEntry = resp;
  } else if (resp && resp.entry) {
    tabelaEntry = resp.entry;
  } else {
    tabelaEntry = [];
  }
  if (!tabelaEntry.length) return [];

  const row0 = tabelaEntry[0];
  const anos = Object.keys(row0).filter((k) => /^\d{4}$/.test(k));
  const dataObject = anos
    .map((ano) => {
      const val = row0[ano];
      let parsed = [];
      try {
        parsed = typeof val === "string" ? JSON.parse(val) : (val || []);
      } catch (e) {
        console.error(`[PdfSerieHistoria] JSON inválido no ano ${ano}:`, e);
        parsed = [];
      }
      return { ano, dados: parsed };
    })
    .sort((a, b) => Number(a.ano) - Number(b.ano));

  return dataObject;
}

// ------------------------ componente ------------------------

export default function PdfSerieHistoria() {
  const dispatch = useDispatch();
  const { numeroCar } = useParams();

  const [objetoPdf, setObjetoPdf] = useState({});
  const [dadosTabela, setDadosTabela] = useState([]);
  const [mapasPorAno, setMapasPorAno] = useState([]);
  const [bbox, setBbox] = useState(null);
  const [headersTabela, setHeadersTabela] = useState(headersFixos);
  const [imovelInfo, setImovelInfo] = useState({});

  const componentRef = useRef(null);

  const legendItems = USO_LEGENDA_ITEMS;

  const cidadeEstadoLegenda = formatCidadeEstado(
    pickNonEmpty(objetoPdf?.nom_munici, imovelInfo?.nom_munici, imovelInfo?.nm_municipio),
    pickNonEmpty(objetoPdf?.cod_estado, imovelInfo?.cod_estado, imovelInfo?.sigla_uf)
  );

  const mapaCidadeUrl = useMemo(() => {
    if (!numeroCar) return "";

    const cod = String(numeroCar).replace(/'/g, "''");
    const baseUrl = `${GEOSERVER_BASE}/lair/wms`;

    const hasWebBbox =
      imovelInfo?.st_xmin != null &&
      imovelInfo?.st_ymin != null &&
      imovelInfo?.st_xmax != null &&
      imovelInfo?.st_ymax != null;

    const bboxParam = hasWebBbox
      ? `${imovelInfo.st_xmin},${imovelInfo.st_ymin},${imovelInfo.st_xmax},${imovelInfo.st_ymax}`
      : bbox
        ? bboxToWebMercator(padBbox(bbox, 0.6))
        : "";

    if (!bboxParam) return "";

    const params = new URLSearchParams({
      service: "WMS",
      request: "GetMap",
      version: "1.1.0",
      layers: `${LAYER_MUNICIPIO},${LAYER_IMOVEL}`,
      format: "image/png",
      transparent: "true",
      srs: "EPSG:900913",
      width: String(IMG_MAPA_CIDADE_W),
      height: String(IMG_MAPA_CIDADE_H),
      bbox: bboxParam,
      cql_filter: `INCLUDE;cod_imovel='${cod}'`,
    });
    const styles = [STYLE_MUNICIPIO, STYLE_IMOVEL].map((s) => s || "").join(",");
    if (styles.replace(/,/g, "").length > 0) {
      params.set("styles", styles);
    }

    return `${baseUrl}?${params.toString()}`;
  }, [bbox, numeroCar, imovelInfo?.st_xmin, imovelInfo?.st_ymin, imovelInfo?.st_xmax, imovelInfo?.st_ymax]);

  const mapaCidadeUrlSemStyle = useMemo(() => {
    if (!numeroCar) return "";

    const cod = String(numeroCar).replace(/'/g, "''");
    const baseUrl = `${GEOSERVER_BASE}/lair/wms`;

    const hasWebBbox =
      imovelInfo?.st_xmin != null &&
      imovelInfo?.st_ymin != null &&
      imovelInfo?.st_xmax != null &&
      imovelInfo?.st_ymax != null;

    const bboxParam = hasWebBbox
      ? `${imovelInfo.st_xmin},${imovelInfo.st_ymin},${imovelInfo.st_xmax},${imovelInfo.st_ymax}`
      : bbox
        ? bboxToWebMercator(padBbox(bbox, 0.6))
        : "";

    if (!bboxParam) return "";

    const params = new URLSearchParams({
      service: "WMS",
      request: "GetMap",
      version: "1.1.0",
      layers: `${LAYER_MUNICIPIO},${LAYER_IMOVEL}`,
      format: "image/png",
      transparent: "true",
      srs: "EPSG:900913",
      width: String(IMG_MAPA_CIDADE_W),
      height: String(IMG_MAPA_CIDADE_H),
      bbox: bboxParam,
      cql_filter: `INCLUDE;cod_imovel='${cod}'`,
    });

    return `${baseUrl}?${params.toString()}`;
  }, [bbox, numeroCar, imovelInfo?.st_xmin, imovelInfo?.st_ymin, imovelInfo?.st_xmax, imovelInfo?.st_ymax]);

  const mapaCidadeUrlImovelOnly = useMemo(() => {
    if (!numeroCar) return "";

    const cod = String(numeroCar).replace(/'/g, "''");
    const baseUrl = `${GEOSERVER_BASE}/lair/wms`;

    const hasWebBbox =
      imovelInfo?.st_xmin != null &&
      imovelInfo?.st_ymin != null &&
      imovelInfo?.st_xmax != null &&
      imovelInfo?.st_ymax != null;

    const bboxParam = hasWebBbox
      ? `${imovelInfo.st_xmin},${imovelInfo.st_ymin},${imovelInfo.st_xmax},${imovelInfo.st_ymax}`
      : bbox
        ? bboxToWebMercator(padBbox(bbox, 0.6))
        : "";

    if (!bboxParam) return "";

    const params = new URLSearchParams({
      service: "WMS",
      request: "GetMap",
      version: "1.1.0",
      layers: `${LAYER_IMOVEL}`,
      format: "image/png",
      transparent: "true",
      srs: "EPSG:900913",
      width: String(IMG_MAPA_CIDADE_W),
      height: String(IMG_MAPA_CIDADE_H),
      bbox: bboxParam,
      cql_filter: `cod_imovel='${cod}'`,
    });

    return `${baseUrl}?${params.toString()}`;
  }, [bbox, numeroCar, imovelInfo?.st_xmin, imovelInfo?.st_ymin, imovelInfo?.st_xmax, imovelInfo?.st_ymax]);


  // Memoriza o Header para não recriar a cada render
  const HeaderPdf = useMemo(() => {
    const cidadeEstado = formatCidadeEstado(
      pickNonEmpty(objetoPdf?.nom_munici, imovelInfo?.nom_munici, imovelInfo?.nm_municipio),
      pickNonEmpty(objetoPdf?.cod_estado, imovelInfo?.cod_estado, imovelInfo?.sigla_uf)
    );
    return () => (
      <Header>
        <LogoEmbrapa src={ImgLogoEmbrapa} />
        <ContainerTitulo>
          <SpanTitulo>Mapa de Histórico de Uso de Solos</SpanTitulo>
          <SpanNumeroCar>{`CAR ${numeroCar}`}</SpanNumeroCar>
          <SpanNomeMunicipioFazenda>{cidadeEstado}</SpanNomeMunicipioFazenda>
        </ContainerTitulo>
      </Header>
    );
  }, [
    numeroCar,
    objetoPdf?.nom_munici,
    objetoPdf?.cod_estado,
    imovelInfo?.nom_munici,
    imovelInfo?.nm_municipio,
    imovelInfo?.cod_estado,
    imovelInfo?.sigla_uf,
  ]);

  useEffect(() => {
    let isMounted = true;

    async function carregarDados() {
      if (!numeroCar) {
        console.error("[PdfSerieHistoria] numeroCar ausente na URL.");
        return;
      }

      dispatch(incrementLoad());
      console.error("[PdfSerieHistoria] Iniciando carga para CAR:", numeroCar);

      try {
        // -------- 1) Imóvel (para obter bbox) --------
        let bboxFromGeom = null;
        try {
          const { data: dadosImovelResp } = await getDadosImovel(numeroCar);
          const imovel = Array.isArray(dadosImovelResp)
            ? dadosImovelResp[0]
            : dadosImovelResp;

          if (!imovel) {
            console.error("[PdfSerieHistoria] Imóvel não encontrado.");
          } else {
            bboxFromGeom = getBoundsFromGeoJSON(imovel.geom);
            if (bboxFromGeom && isMounted) setBbox(bboxFromGeom);
            if (isMounted) {
              setImovelInfo({
                nom_munici: imovel?.nom_munici ?? "",
                nm_municipio: imovel?.nm_municipio ?? "",
                cod_estado: imovel?.cod_estado ?? "",
                sigla_uf: imovel?.sigla_uf ?? "",
                st_xmin: imovel?.st_xmin ?? null,
                st_ymin: imovel?.st_ymin ?? null,
                st_xmax: imovel?.st_xmax ?? null,
                st_ymax: imovel?.st_ymax ?? null,
              });
            }
          }
        } catch (e) {
          console.error("[PdfSerieHistoria] Erro ao buscar Imóvel:", e);
        }

        // -------- 2) Série Histórica (metadados/legendas/estado) --------
        try {
          const { data: serieResp } = await getDadosSerieHistoria(numeroCar);
          const { meta } = parseSerieHistoria(serieResp);
          if (isMounted) {
            setObjetoPdf(meta || {});
          }
        } catch (err) {
          const status = err?.response?.status;
          const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
          console.error("[PdfSerieHistoria] Erro ao buscar Série Histórica:", status, serverMsg || err?.message);
          if (isMounted) {
            setObjetoPdf({});
          }
        }

        // -------- 3) Monta as imagens WMS por ano (DINÂMICO, NOVO) --------
        if (bboxFromGeom) {
          const bPad = padBbox(bboxFromGeom, 0.07);
          const cqlCar = `cod_imovel='${String(numeroCar).replace(/'/g, "''")}'`;

          const imagens = [
            {
              ano: "1985",
              img: wmsPngUrl({
                layer: LAYER_USO_1985,
                style: STYLE_USO_1985,
                bbox: bPad,
                cql: cqlCar,
                width: IMG_W,
                height: IMG_H,
              }),
            },
            {
              ano: "2008",
              img: wmsPngUrl({
                layer: LAYER_USO_2008,
                style: STYLE_USO_2008,
                bbox: bPad,
                cql: cqlCar,
                width: IMG_W,
                height: IMG_H,
              }),
            },
            {
              ano: "2011",
              img: wmsPngUrl({
                layer: LAYER_USO_2011,
                style: STYLE_USO_2011,
                bbox: bPad,
                cql: cqlCar,
                width: IMG_W,
                height: IMG_H,
              }),
            },
            {
              ano: "2016",
              img: wmsPngUrl({
                layer: LAYER_USO_2016,
                style: STYLE_USO_2016,
                bbox: bPad,
                cql: cqlCar,
                width: IMG_W,
                height: IMG_H,
              }),
            },
            {
              ano: "2021",
              img: wmsPngUrl({
                layer: LAYER_USO_2021,
                style: STYLE_USO_2021,
                bbox: bPad,
                cql: cqlCar,
                width: IMG_W,
                height: IMG_H,
              }),
            },
          ];

          // Mesmo agrupamento que o arquivo original usa (12 por folha)
          const agrupado = agruparEmArrays(imagens, 12);
          if (isMounted) setMapasPorAno(agrupado);
        } else {
          if (isMounted) setMapasPorAno([]);
        }

        // -------- 4) Tabela (histórico por uso) --------
        try {
          const { data: tabelaResp } = await getDadosTabelaHistoricoUsoSolo(numeroCar);
          const dataObject = parseTabelaHistorico(tabelaResp);
          if (isMounted) {
            setDadosTabela(dataObject);
            setHeadersTabela(headersFixos);
          }
        } catch (err) {
          const status = err?.response?.status;
          const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
          console.error("[PdfSerieHistoria] Erro ao buscar Tabela Histórico:", status, serverMsg || err?.message);
          if (isMounted) {
            setDadosTabela([]);
          }
        }
      } catch (e) {
        console.error("[PdfSerieHistoria] Erro geral na carga:", e);
      } finally {
        dispatch(decrementLoad());
      }
    }

    carregarDados();

    return () => {
      isMounted = false;
    };
  }, [numeroCar, dispatch]);

  return (
    <div
      style={{
        height: "100vh",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <ReactToPrint
        documentTitle="Mapa de Histórico de Uso de Solos"
        onBeforePrint={() => dispatch(incrementLoad())}
        onAfterPrint={() => dispatch(decrementLoad())}
        trigger={() => (
          <div
            style={{
              width: "100vw",
              alignItems: "center",
              backgroundColor: "red",
              position: "relative",
              marginBottom: "50px",
              marginTop: "20px",
            }}
          >
            <Button variant="contained" style={{ position: "absolute", left: "50%" }}>
              Mapa de Histórico de Uso de Solos
            </Button>
          </div>
        )}
        content={() => componentRef.current}
      />

      <div style={{ backgroundColor: "#ECF0F6" }} ref={componentRef}>
        <div>
          {!!mapasPorAno.length &&
            mapasPorAno.map((mapasFolhaAtual, idxFolha) => (
              <FolhaA4 key={`folha-${idxFolha}`}>
                {HeaderPdf()}
                <CorpoPdf>
                  <ImgsContainer>
                    <MapsContainer>
                      {mapasFolhaAtual?.map((mp) => (
                        <ContainerAnoImg key={`img-${mp.ano}`}>
                          <TextoAno>{mp.ano}</TextoAno>
                          <img
                            src={mp.img}
                            alt={`Uso ${mp.ano}`}
                            width={160}
                            height={160}
                            onError={(e) => {
                              console.error("[PdfSerieHistoria] imagem falhou:", mp);
                              e.currentTarget.style.opacity = 0.3;
                            }}
                          />
                        </ContainerAnoImg>
                      ))}
                    </MapsContainer>

                    <LegendaEstadoContainer>
                      <ContainerAnoImg>
                        <TextoLegenda>Cobertura e uso da terra</TextoLegenda>
                        {(
                          <div
                            style={{
                              width: 150,
                              background: "white",
                              borderRadius: 6,
                              border: "1px solid rgba(0,0,0,0.15)",
                              padding: 8,
                              fontSize: 11,
                              lineHeight: 1.2,
                            }}
                          >
                            <div style={{ textAlign: "center", fontWeight: 600, marginBottom: 6 }}>
                              Uso do Solo
                            </div>
                            {legendItems.map((it) => (
                              <div
                                key={it.key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  margin: "4px 0",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 12,
                                    height: 12,
                                    borderRadius: 2,
                                    border: "1px solid rgba(0,0,0,0.3)",
                                    background: it.color,
                                    flex: "0 0 auto",
                                  }}
                                />
                                <span style={{ color: "#000" }}>{it.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <TextoLegendaNota>
                          Parâmetros da legenda são fixos devido a limitações do GeoServer.
                        </TextoLegendaNota>
                      </ContainerAnoImg>

                      <ContainerAnoImg>
                        <TextoCidadeEstadoContainer>
                          <TextoNomeCidade>{cidadeEstadoLegenda}</TextoNomeCidade>
                        </TextoCidadeEstadoContainer>

                        {mapaCidadeUrl ? (
                          <img
                            src={mapaCidadeUrl}
                            alt="Mapa do municipio"
                            width={IMG_MAPA_CIDADE_W}
                            height={IMG_MAPA_CIDADE_H}
                            onError={(e) => {
                              const img = e.currentTarget;
                              const etapa = img.getAttribute("data-style-fallback") || "0";
                              if (etapa === "0" && mapaCidadeUrlSemStyle) {
                                console.warn(
                                  "[PdfSerieHistoria] Fallback mapa cidade: sem styles",
                                  mapaCidadeUrlSemStyle
                                );
                                img.setAttribute("data-style-fallback", "1");
                                img.src = mapaCidadeUrlSemStyle;
                                return;
                              }
                              if (etapa === "1" && mapaCidadeUrlImovelOnly) {
                                console.warn(
                                  "[PdfSerieHistoria] Fallback mapa cidade: somente imovel",
                                  mapaCidadeUrlImovelOnly
                                );
                                img.setAttribute("data-style-fallback", "2");
                                img.src = mapaCidadeUrlImovelOnly;
                                return;
                              }
                              console.warn("[PdfSerieHistoria] Mapa cidade falhou em todos os fallbacks.");
                              img.style.opacity = 0.3;
                            }}
                          />
                        ) : objetoPdf?.imagemestado ? (
                          <img
                            src={objetoPdf.imagemestado}
                            alt="Estado"
                            width={IMG_MAPA_CIDADE_W}
                            height={IMG_MAPA_CIDADE_H}
                            onError={(e) => (e.currentTarget.style.opacity = 0.3)}
                          />
                        ) : null}
                      </ContainerAnoImg>

                      <ContainerAnoImg style={{ width: 300, height: 80 }}>
                        <EscalaGrafica bbox={bbox} />
                      </ContainerAnoImg>
                    </LegendaEstadoContainer>
                  </ImgsContainer>

                  <ContainerTextoRodape>
                    <TextoRodape>
                      FONTE: Mapeamento de cobertura e uso da terra do Projeto Mapbiomas e limite do imóvel rural do Cadastro Ambiental Rural - CAR
                      Embrapa: Sistema de apoio à caracterização ambiental de imóveis rurais.
                      <br />
                      http://sacir.cnpma.embrapa.com.br
                    </TextoRodape>
                  </ContainerTextoRodape>
                </CorpoPdf>
              </FolhaA4>
            ))}

          <FolhaA4>
            <PDFContainer>
              {HeaderPdf()}
              <TabelaContainer>
                <TextoInfoTabela>
                  {"Histórico das áreas de cobertura e uso da terra do imóvel rural desde 1985, por tipo de uso e cobertura. Área em hectares(ha)."}
                </TextoInfoTabela>
                <Tabela>
                  <tbody>
                    <LinhaTabela>
                      <CelulaTabela>
                        <TextoAnoTabela>Ano</TextoAnoTabela>
                      </CelulaTabela>
                      {headersTabela.map((item, index) => (
                        <CelulaTabela key={index}>
                          <TextoHeaderTabela>{item}</TextoHeaderTabela>
                        </CelulaTabela>
                      ))}
                    </LinhaTabela>

                    {dadosTabela.map((dado, index) => (
                      <LinhaTabela key={index}>
                        <CelulaTabela>{dado.ano}</CelulaTabela>
                        {headersTabela.map((h, i) => {
                          const celula = dado.dados.find((d) => dePara(h, d.uso));
                          return (
                            <CelulaTabela key={i}>
                              {celula ? parseFloat(celula.area).toFixed(2) : "0.00"}
                            </CelulaTabela>
                          );
                        })}
                      </LinhaTabela>
                    ))}
                  </tbody>
                </Tabela>

                <TextoInfoTabela>
                  {"Fonte: Mapeamento de cobertura e uso da terra do Projeto Mapbiomas e Limite do imóvel rural do Cadastro Ambiental Rural - CAR."}
                </TextoInfoTabela>
              </TabelaContainer>

              <RodapePdf>
                <ContainerTextoRodapeH>
                  <TextoRodapePdf>Embrapa. Sistema de apoio à caracterização ambiental de imóveis rurais.</TextoRodapePdf>
                  <TextoRodapePdf>http://sacir.cnpma.embrapa.com.br</TextoRodapePdf>
                </ContainerTextoRodapeH>
                <TextoRodapePdf>{new Date().toLocaleDateString()}</TextoRodapePdf>
              </RodapePdf>
            </PDFContainer>
          </FolhaA4>
        </div>
      </div>
    </div>
  );
}
